import React, { useState } from 'react';
import { toast } from '../../../../../../ui/toast';
import { createJDTemplate } from '../../../../../../utils/api_service/JobOpenings';
import { JobDescription, JobDescriptionTemplate } from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';

interface CreateTemplateProps {
    closeModal: () => void,
    jobDescription: JobDescription,
    onTemplateSave?: (templateData: JobDescriptionTemplate) => void,
    jobTitle: SelectOptions | undefined
}

export const CreateTemplate = ({ closeModal, jobDescription, onTemplateSave, jobTitle }: CreateTemplateProps) => {

  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = today.toLocaleString('en-GB', { month: 'short' }); // e.g. "Nov"
  const year = today.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  const [templateName, setTemplateName] = useState(
    jobTitle?.label ? `${jobTitle.label} - ${formattedDate}` : '',
  );

  const [templateDescription, setTemplateDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag) && tags.length < 8) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {

    setIsSaving(true);

    const templateData: JobDescriptionTemplate = {
      templateTitle: templateName,
      templateDescription: templateDescription.trim(),
      tags: tags,
      jobTitle: typeof jobDescription.jobTitle === 'object'
        ? jobDescription.jobTitle?.id
        : jobDescription.jobTitle,
      workType: jobDescription.workType,
      minimumWorkExperience: jobDescription.minimumWorkExperience,
      jobLocation: jobDescription.jobLocation,
      responsibilities: jobDescription.responsibilities,
      educations: jobDescription.educations,
      certifications: jobDescription.certifications,
      mandatorySkills: jobDescription.mandatorySkills,
      desirableSkills: jobDescription.desirableSkills,
      behavioralAttributes: jobDescription.behavioralAttributes,
      aiScoreWeights: jobDescription.aiScoreWeights,
      documentRequirements: jobDescription.documentRequirements,
    };

    try {
      const response = await createJDTemplate(templateData);
      if (response.ok) {
        toast.success('Template Created!');
      } else {
        toast.error('Error saving templates', { position: 'bottom-center' });
      }
      onTemplateSave?.(templateData);
      closeModal();
    } catch (error) {
      toast.error('Error saving templates', { position: 'bottom-center' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='flex flex-col h-full w-full max-w-6xl mx-auto'>

      { /* Form */ }
      <div className='flex-1 space-y-6'>
        { /* Template Name */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Template Name *
          </label>
          <input
              type='text'
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder='e.g., Senior Frontend Developer Template'
              className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
              maxLength={100}
          />
          <div className='text-xs text-gray-500 mt-1 px-2'>
            { templateName.length }/100 characters
          </div>
        </div>

        { /* Template Description */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Description *
          </label>
          <textarea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder='Describe what this template is for and when to use it...'
              rows={3}
              className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none'
              maxLength={300}
          />
          <div className='text-xs text-gray-500 mt-1 px-2'>
            { templateDescription.length }/300 characters
          </div>
        </div>

        { /* Tags */ }
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Tags (Optional)
          </label>
          <div className='space-y-3'>
            <input
                type='text'
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder='Type a tag and press Enter (e.g., React, JavaScript, Senior Level)'
                className='w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                disabled={tags.length >= 8}
            />
            { tags.length > 0 && (
              <div className='flex flex-wrap gap-2'>
                { tags.map((tag, index) => (
                  <span
                      key={index}
                      className='inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full'
                  >
                    { tag }
                    <button
                        onClick={() => removeTag(tag)}
                        className='text-indigo-600 hover:text-indigo-800'
                    >
                      <svg className='w-3 h-3' fill='currentColor' viewBox='0 0 20 20'>
                        <path fillRule='evenodd'
                            d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                            clipRule='evenodd'/>
                      </svg>
                    </button>
                  </span>
                )) }
              </div>
            ) }
            <div className='text-xs text-gray-500 px-2'>
              { tags.length }/8 tags added. Press Enter or comma to add tags.
            </div>
          </div>
        </div>

        { /* Privacy Setting */ }
        { /*<div>*/ }
        { /*  <div className='flex items-start gap-3'>*/ }
        { /*    <div className='flex items-center h-5'>*/ }
        { /*      <input*/ }
        { /*          id='private-template'*/ }
        { /*          type='checkbox'*/ }
        { /*          checked={isPrivate}*/ }
        { /*          onChange={(e) => setIsPrivate(e.target.checked)}*/ }
        { /*          className='w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2'*/ }
        { /*      />*/ }
        { /*    </div>*/ }
        { /*    <div>*/ }
        { /*      <label htmlFor='private-template' className='text-sm font-medium text-gray-700'>*/ }
        { /*        Make this template private*/ }
        { /*      </label>*/ }
        { /*      <p className='text-xs text-gray-500 mt-1'>*/ }
        { /*        Private templates are only visible to you. Uncheck to share with your team.*/ }
        { /*      </p>*/ }
        { /*    </div>*/ }
        { /*  </div>*/ }
        { /*</div>*/ }

        { /* Template Preview */ }
        <div className='bg-gray-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-gray-900 mb-3'>Template will include:</h4>
          <div className='grid grid-cols-2 gap-4 text-sm'>
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>Job Title Configuration</span>
              </div>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>Work Type & Experience</span>
              </div>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>Location Settings</span>
              </div>
            </div>
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>Skills & Requirements</span>
              </div>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>AI Score Weightings</span>
              </div>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>Document Configuration</span>
              </div>
              <div className='flex items-center gap-2'>
                <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'/>
                </svg>
                <span className='text-gray-700'>All Lists & Attributes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      { /* Action Buttons */ }
      <div className='flex justify-between items-center border-t border-gray-200 pt-6'>
        <button
            onClick={closeModal}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
            disabled={isSaving}
        >
          Cancel
        </button>
        <button
            onClick={handleSave}
            disabled={!templateName.trim() || !templateDescription.trim() || isSaving}
            className={`px-6 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
              templateName.trim() && templateDescription.trim() && !isSaving
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
        >
          { isSaving && (
            <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
              <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor'
                  strokeWidth='4'/>
              <path className='opacity-75' fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'/>
            </svg>
          ) }
          { isSaving ? 'Saving Template...' : 'Save Template' }
        </button>
      </div>
    </div>
  );
};
